import React, { useState, useEffect } from 'react';
import { Settings, Lock, ShieldCheck, Play, Clock, CheckCircle, Star, Quote } from 'lucide-react';

const HowItWorks: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    
    // Auto-cycle through steps
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 3);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const steps = [
    {
      number: 1,
      icon: <Settings className="w-8 h-8 text-white" />,
      title: 'تركيب سهل',
      arabicTitle: 'خطوة التركيب',
      description: 'ضع الجهاز على الدواسة في أقل من دقيقتين',
      details: 'لا يحتاج أدوات خاصة أو خبرة تقنية',
      image: 'https://i.ibb.co/Zp396GD2/Whats-App-Image-2025-09-12-at-15-30-53.jpg',
      color: 'bg-blue-500',
      time: '2 دقيقة'
    },
    {
      number: 2,
      icon: <Lock className="w-8 h-8 text-white" />,
      title: 'قفل آمن',
      arabicTitle: 'خطوة الحماية',
      description: 'اقفل الجهاز بالمفتاح المرفق معه',
      details: 'مفتاح مقاوم للكسر وتصميم مكافح للسرقة',
      image: 'https://i.ibb.co/4ZmHmKJd/Whats-App-Image-2025-09-10-at-3-04-51-PM.jpg',
      color: 'bg-orange-500',
      time: '10 ثواني'
    },
    {
      number: 3,
      icon: <ShieldCheck className="w-8 h-8 text-white" />,
      title: 'حماية كاملة',
      arabicTitle: 'خطوة الأمان',
      description: 'سيارتك محمية بالكامل ضد السرقة',
      details: 'حماية فعالة 99.9% ومجربة من آلاف العملاء',
      image: 'https://i.ibb.co/whNMSK8j/image-11.png',
      color: 'bg-green-500',
      time: '24/7'
    }
  ];

  return (
    <section id="how-it-works" className="py-16 lg:py-24 bg-gradient-to-br from-gray-100 via-white to-blue-100 relative overflow-hidden">
      
      {/* Background Decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200 rounded-full blur-3xl opacity-20 animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-green-200 rounded-full blur-3xl opacity-20 animate-pulse-slow"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-6 relative z-10">
        
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Settings className="w-4 h-4" />
            <span>طريقة عمل بسيطة</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-800 mb-6">
            <span className="gradient-text">تركيب سهل</span>
            <span className="block text-gray-800">في 3 خطوات</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            احمِ سيارتك في أقل من 3 دقائق. عملية بسيطة وآمنة تضمن لك حماية فائقة ضد السرقة
          </p>
        </div>

        {/* Main Steps Display */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
          
          {/* Interactive Steps */}
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`cursor-pointer transition-all duration-500 ${
                  activeStep === index || isVisible ? 'animate-slide-up' : 'opacity-0'
                }`}
                style={{ animationDelay: `${index * 200}ms` }}
                onClick={() => setActiveStep(index)}
              >
                <div className={`card-modern p-6 lg:p-8 hover-lift ${
                  activeStep === index 
                    ? 'ring-4 ring-blue-200 shadow-2xl scale-105' 
                    : 'hover:shadow-xl'
                }`}>
                  
                  <div className="flex items-start gap-6">
                    {/* Step Icon */}
                    <div className={`flex-shrink-0 w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 ${
                      activeStep === index ? 'scale-110 shadow-glow' : ''
                    }`}>
                      {step.icon}
                    </div>
                    
                    {/* Step Content */}
                    <div className="flex-1 text-right">
                      <div className="flex items-center justify-between mb-3">
                        <div className={`px-3 py-1 ${step.color} text-white rounded-full text-sm font-semibold`}>
                          {step.time}
                        </div>
                        <div className="text-sm text-gray-500">خطوة {step.number}</div>
                      </div>
                      
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">{step.arabicTitle}</h3>
                      <h4 className="text-lg text-gray-600 mb-3">{step.title}</h4>
                      
                      <p className="text-gray-600 text-lg leading-relaxed mb-3">
                        {step.description}
                      </p>
                      
                      <p className="text-sm text-gray-500 italic">
                        {step.details}
                      </p>
                      
                      {/* Progress Bar */}
                      <div className="mt-4 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-full ${step.color} transition-all duration-1000 ${
                            activeStep === index ? 'w-full' : 'w-0'
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Visual Display */}
          <div className="relative">
            <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
              
              {/* Main Image */}
              <div className="relative aspect-square overflow-hidden rounded-2xl bg-gray-50 mb-6">
                <img 
                  src={steps[activeStep].image}
                  alt={steps[activeStep].arabicTitle}
                  className="w-full h-full object-cover transition-all duration-500 animate-float"
                />
                
                {/* Floating Step Number */}
                <div className={`absolute top-4 right-4 w-12 h-12 ${steps[activeStep].color} rounded-full flex items-center justify-center shadow-lg`}>
                  <span className="text-white font-bold text-xl">{steps[activeStep].number}</span>
                </div>
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                  <button className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300">
                    <Play className="w-8 h-8 text-gray-700 ml-1" />
                  </button>
                </div>
              </div>
              
              {/* Step Indicators */}
              <div className="flex justify-center space-x-3">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveStep(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      activeStep === index 
                        ? `${steps[index].color} w-8` 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-6 -left-6 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center shadow-lg animate-bounce-slow">
              <Clock className="w-6 h-6 text-white" />
            </div>
            
            <div className="absolute -bottom-6 -right-6 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg animate-bounce-slow">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="text-center">
          <h3 className="text-3xl font-bold text-gray-800 mb-12">لماذا هذه الطريقة فعالة؟</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Clock className="w-8 h-8 text-blue-600" />,
                title: 'سرعة التركيب',
                description: 'تركيب فوري في أقل من 3 دقائق بدون أدوات',
                color: 'bg-blue-100 text-blue-600'
              },
              {
                icon: <ShieldCheck className="w-8 h-8 text-green-600" />,
                title: 'حماية فورية',
                description: 'حماية كاملة للسيارة فور انتهاء التركيب',
                color: 'bg-green-100 text-green-600'
              },
              {
                icon: <Settings className="w-8 h-8 text-purple-600" />,
                title: 'سهولة الاستخدام',
                description: 'لا يحتاج خبرة تقنية أو معرفة خاصة',
                color: 'bg-purple-100 text-purple-600'
              }
            ].map((benefit, index) => (
              <div
                key={index}
                className={`card-modern p-8 text-center hover-lift ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
                style={{ animationDelay: `${(index + 3) * 200}ms` }}
              >
                <div className={`w-16 h-16 mx-auto mb-6 ${benefit.color} rounded-2xl flex items-center justify-center`}>
                  {benefit.icon}
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-4">{benefit.title}</h4>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Customer Testimonial Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">شهادات العملاء</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              آراء حقيقية من عملاء استخدموا منتجاتنا وشعروا بالأمان التام
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Testimonial Image */}
              <div className="relative">
                <img 
                  src="https://i.ibb.co/1GGHqVps/Whats-App-Image-2025-11-15-at-22-18-57.jpg" 
                  alt="Customer Testimonial" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Testimonial Content */}
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="mb-6">
                  <Quote className="w-12 h-12 text-blue-500 opacity-20" />
                </div>
                
                <h4 className="text-2xl font-bold text-gray-800 mb-4">تجربة ممتازة!</h4>
                
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  "لقد استخدمت بلوك الدواسة وأنا سعيد جداً بالنتيجة. التركيب كان سهلاً جداً واستغرق أقل من دقيقتين. الآن أشعر بالأمان التام عند ترك سيارتي في أي مكان. أنصح بهذا المنتج بشدة لكل من يريد حماية سيارته."
                </p>
                
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                <div className="text-gray-700">
                  <div className="font-semibold">أحمد بن علي</div>
                  <div className="text-sm">تونس</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Video Section */}
        <div className="mt-20 text-center">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-800 mb-8">شاهد طريقة التركيب</h3>
            
            <div className="relative bg-gray-900 rounded-3xl overflow-hidden shadow-2xl">
              <div className="aspect-video">
                <iframe
                  src="https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1522394962350685%2F&show_text=false&width=267&t=0"
                  className="w-full h-full"
                  style={{ border: "none", overflow: "hidden" }}
                  scrolling="no"
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              
              {/* Video Overlay Info */}
              <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-70 rounded-xl p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Play className="w-5 h-5" />
                    <div className="text-right">
                      <div className="font-semibold">فيديو توضيحي - طريقة التركيب</div>
                      <div className="text-sm opacity-90">تركيب سهل في 3 دقائق فقط</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">3 دقائق</div>
                    <div className="text-xs">مدة التركيب</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;